<div class="wo-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
  <!-- Header -->
  <div class="mb-8">
    <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 class="mb-2 flex items-center gap-3 text-3xl font-bold text-gray-900">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-green-600 shadow-sm">
            <mat-icon class="!text-xl text-white">description</mat-icon>
          </div>
          Work Orders
        </h1>
        <p class="text-gray-600">Manage work orders for project planning</p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <div class="min-w-[140px] rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100">
              <mat-icon class="!text-xl text-sky-600">assignment</mat-icon>
            </div>
            <div>
              <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Total</p>
              <p class="text-xl font-semibold text-gray-900">{{ totalItems }}</p>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="btn-primary"
          (click)="createNew()"
        >
          <mat-icon class="btn-icon">add</mat-icon>
          New Work Order
        </button>

        <button
          type="button"
          class="btn-secondary"
          (click)="loadWorkOrders()"
          [disabled]="loading"
        >
          <mat-icon class="btn-icon" [class.spin]="loading">refresh</mat-icon>
          Refresh
        </button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
      <div class="md:col-span-5">
        <label class="field-label" for="wo-search">Search</label>
        <div class="search-wrap">
          <div class="search-icon">
            <mat-icon class="!text-lg text-gray-400">search</mat-icon>
          </div>
          <input
            id="wo-search"
            type="text"
            class="filter-control filter-control--search"
            [(ngModel)]="searchKeyword"
            (keyup.enter)="applyFilter()"
            placeholder="WO number, client, vendor…"
          />
        </div>
      </div>

      <div class="md:col-span-3">
        <label class="field-label" for="wo-status">Status</label>
        <select
          id="wo-status"
          class="filter-control"
          [(ngModel)]="selectedStatus"
          (change)="applyFilter()"
        >
          <option *ngFor="let status of statusOptions" [value]="status.value">
            {{ status.label }}
          </option>
        </select>
      </div>

      <div class="flex items-end gap-2 md:col-span-4">
        <button type="button" class="btn-secondary flex-1 md:flex-none" (click)="applyFilter()">
          Apply
        </button>
        <button type="button" class="btn-secondary flex-1 md:flex-none" (click)="clearFilters()">
          <mat-icon class="btn-icon !text-base">clear</mat-icon>
          Clear
        </button>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="rounded-2xl border border-gray-200 bg-white py-16 text-center shadow-lg">
    <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-sky-600 border-t-transparent"></div>
    <p class="text-sm text-gray-600">Loading work orders…</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading" class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
    <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
      <h2 class="text-lg font-semibold text-gray-900">Work order overview</h2>
      <p class="mt-1 text-sm text-gray-600">View, edit, and share work orders across projects</p>
    </div>

    <div *ngIf="dataSource.data.length === 0" class="empty-state px-6 py-16 text-center">
      <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100">
        <mat-icon class="!text-3xl text-green-600">description</mat-icon>
      </div>
      <h3 class="mb-2 text-lg font-semibold text-gray-900">No Work Orders Found</h3>
      <p class="mx-auto mb-6 max-w-md text-gray-600">Create your first work order to get started with project planning.</p>
      <button type="button" class="btn-primary" (click)="createNew()">
        <mat-icon class="btn-icon">add</mat-icon>
        Create Work Order
      </button>
    </div>

    <div *ngIf="dataSource.data.length > 0" class="overflow-x-auto">
      <table mat-table [dataSource]="dataSource" matSort class="wo-table w-full">
        <ng-container matColumnDef="work_order_number">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="wo-th">Work Order #</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td">
            <span class="font-medium text-sky-700">{{ workOrder.work_order_number }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="project_name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="wo-th">Project</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td">
            <span class="font-medium text-gray-900">{{ workOrder.project_name || 'N/A' }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="client_name">
          <th mat-header-cell *matHeaderCellDef class="wo-th">Client</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td text-gray-700">
            {{ workOrder.client_name }}
          </td>
        </ng-container>

        <ng-container matColumnDef="vendor_name">
          <th mat-header-cell *matHeaderCellDef class="wo-th">Vendor</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td text-gray-700">
            {{ workOrder.vendor_name }}
          </td>
        </ng-container>

        <ng-container matColumnDef="issue_date">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="wo-th">Issue Date</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td text-gray-600">
            {{ workOrder.issue_date ? (workOrder.issue_date | date:'mediumDate') : 'N/A' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="total_amount">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="wo-th">Total Amount</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td">
            <span class="font-semibold text-gray-900">{{ formatCurrency(workOrder.total_amount) }}</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="wo-th">Status</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td">
            <div class="flex flex-col gap-1.5">
              <span [class]="'status-badge ' + getStatusClass(workOrder.status)">
                {{ workOrder.status | titlecase }}
              </span>
              <div class="flex gap-1">
                <mat-icon
                  *ngIf="workOrder.is_shared_with_client"
                  class="meta-icon text-emerald-500"
                  matTooltip="Shared with client"
                >share</mat-icon>
                <mat-icon
                  *ngIf="workOrder.email_sent"
                  class="meta-icon text-sky-500"
                  matTooltip="Email sent"
                >email</mat-icon>
                <mat-icon
                  *ngIf="workOrder.pdf_generated"
                  class="meta-icon text-red-500"
                  matTooltip="PDF generated"
                >picture_as_pdf</mat-icon>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="wo-th wo-th--actions">Actions</th>
          <td mat-cell *matCellDef="let workOrder" class="wo-td wo-td--actions">
            <div class="action-group">
              <button mat-icon-button matTooltip="View" class="action-btn" (click)="viewDetails(workOrder)">
                <mat-icon>visibility</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Edit" class="action-btn" (click)="editWorkOrder(workOrder)">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Generate PDF" class="action-btn" (click)="generatePDF(workOrder)">
                <mat-icon>picture_as_pdf</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Share with Client" class="action-btn" (click)="shareWithClient(workOrder)">
                <mat-icon>share</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Send Email" class="action-btn" (click)="sendEmail(workOrder)">
                <mat-icon>email</mat-icon>
              </button>
              <button mat-icon-button matTooltip="Delete" class="action-btn action-btn--danger" (click)="deleteWorkOrder(workOrder)">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns" class="wo-header-row"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns" class="wo-row"></tr>
      </table>
    </div>

    <div *ngIf="dataSource.data.length > 0" class="border-t border-gray-100 bg-gray-50/80 px-2 py-1">
      <mat-paginator
        [length]="totalItems"
        [pageSize]="pageSize"
        [pageIndex]="pageIndex"
        [pageSizeOptions]="pageSizeOptions"
        [showFirstLastButtons]="true"
        (page)="onPageChange($event)"
        class="wo-paginator"
      ></mat-paginator>
    </div>
  </div>
</div>
